import React from 'react';
import { clampPercent, titleCase } from '../utils/format';
import './courseCard.css';

/**
 * Icons are Heroicons (outline, https://heroicons.com), drawn on a 24x24
 * grid at their native stroke-width of 1.5 — see
 * .superpowers/sdd/2026-08-10-visual-redesign-shell-dashboard/icons-heroicons.md
 * for the source paths (Bookmark, Remove bookmark, Share); Check,
 * MoreHorizontal, and Trash were sourced separately from the same set. Keep
 * any future additions to this file sourced the same way so the set stays
 * visually consistent with the sidebar (src/layout/navItems.jsx).
 */
function BookmarkIcon(props) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0Z" />
    </svg>
  );
}

function RemoveBookmarkIcon(props) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="m3 3 1.664 1.664M21 21l-1.5-1.5m-5.485-1.242L12 17.25 4.5 21V8.742m.164-4.078a2.15 2.15 0 0 1 1.743-1.342 48.507 48.507 0 0 1 11.186 0c1.1.128 1.907 1.077 1.907 2.185V19.5M4.664 4.664 19.5 19.5" />
    </svg>
  );
}

function ShareIcon(props) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z" />
    </svg>
  );
}

function CheckIcon(props) {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
    </svg>
  );
}

function MoreHorizontalIcon(props) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM12.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM18.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" />
    </svg>
  );
}

function TrashIcon(props) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
    </svg>
  );
}

/** Formats an ISO timestamp as `Aug 2`. Returns '' when absent or unparseable. */
export function formatShortDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * @typedef {Object} CourseCardProps
 * @property {Object}   course        CourseOverviewDto — courseId, name, desc, completionPercentage,
 *                                     tag, difficulty, nextUpSection, bookmarked, createdAt, lastAccessedAt, acceptCount.
 * @property {'grid'|'row'} [variant]  'grid' (default) for CardsView, 'row' for ListView.
 * @property {boolean}  bookmarked    Effective bookmark state after optimistic overrides.
 * @property {() => void} onOpen       Navigate into the course.
 * @property {() => void} onToggleBookmark
 * @property {() => void} onShare
 * @property {() => void} onDelete
 * @property {boolean}  menuOpen
 * @property {(next: boolean) => void} onMenuOpenChange
 */
export default function CourseCard({
  course,
  variant = 'grid',
  bookmarked,
  onOpen,
  onToggleBookmark,
  onShare,
  onDelete,
  menuOpen,
  onMenuOpenChange,
}) {
  const pct = clampPercent(course.completionPercentage);
  const isComplete = pct >= 100;
  const notStarted = pct <= 0;

  const activate = (e) => {
    // Ignore activation that bubbled out of the kebab menu.
    if (e.target.closest('.course-card-menu')) return;
    onOpen();
  };

  return (
    <article
      className={`course-card ${variant === 'row' ? 'is-row' : 'is-grid'}`}
      tabIndex={0}
      onClick={activate}
      onKeyDown={(e) => { if (e.key === 'Enter') activate(e); }}
    >
      <header className="course-card-meta">
        {isComplete && (
          <span className="course-card-check" aria-hidden="true">
            <CheckIcon />
          </span>
        )}
        {bookmarked && <BookmarkIcon className="course-card-bookmark" aria-label="Bookmarked" />}
        <span className="course-card-chip">{titleCase(course.tag)}</span>
        <span className="course-card-difficulty">{titleCase(course.difficulty)}</span>
        {course.acceptCount > 0 && (
          <span className="course-card-chip">
            <ShareIcon width="10" height="10" /> Shared · {course.acceptCount}
          </span>
        )}
        <span className="course-card-spacer" />
        <div className={`course-card-menu ${menuOpen ? 'is-open' : ''}`}>
          <button
            type="button"
            className="course-card-kebab"
            aria-label="Course actions"
            aria-haspopup="menu"
            aria-expanded={menuOpen ? 'true' : 'false'}
            onClick={(e) => { e.stopPropagation(); onMenuOpenChange(!menuOpen); }}
          >
            <MoreHorizontalIcon />
          </button>
          {menuOpen && (
            <div
              role="menu"
              aria-label="Course actions menu"
              className="menu-card"
              // The dashboard closes menus on a document-level `mousedown`.
              // Without this guard the menu closes on the item's own mousedown
              // and unmounts before the click lands, so the action never fires.
              onMouseDown={(e) => e.stopPropagation()}
            >
              <button role="menuitem" className="menu-item" onClick={(e) => { e.stopPropagation(); onToggleBookmark(); }}>
                {bookmarked ? <RemoveBookmarkIcon /> : <BookmarkIcon width="16" height="16" />} <span>{bookmarked ? 'Remove Bookmark' : 'Bookmark Course'}</span>
              </button>
              <button role="menuitem" className="menu-item" onClick={(e) => { e.stopPropagation(); onShare(); }}>
                <ShareIcon /> <span>Share Course</span>
              </button>
              <div className="menu-sep" />
              <button role="menuitem" className="menu-item is-danger" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
                <TrashIcon /> <span>Delete Course</span>
              </button>
            </div>
          )}
        </div>
      </header>

      <h3 className="course-card-title">{course.name}</h3>
      <p className="course-card-desc">{course.desc}</p>

      <div className="course-card-spacer" />

      <footer className="course-card-footer">
        {isComplete ? (
          <div className="course-card-footer-split">
            <div>
              <p className="course-card-status is-complete">
                <span className="course-card-check" aria-hidden="true"><CheckIcon /></span>
                Completed · 100%
              </p>
              {formatShortDate(course.lastAccessedAt) && (
                <p className="course-card-sub">Last opened {formatShortDate(course.lastAccessedAt)}</p>
              )}
            </div>
            <button type="button" className="course-card-action" onClick={(e) => { e.stopPropagation(); onOpen(); }}>
              Review course
            </button>
          </div>
        ) : notStarted ? (
          <div className="course-card-footer-split">
            <div>
              <p className="course-card-status">Not started</p>
              {formatShortDate(course.createdAt) && (
                <p className="course-card-sub">Created {formatShortDate(course.createdAt)}</p>
              )}
            </div>
            <button type="button" className="course-card-action" onClick={(e) => { e.stopPropagation(); onOpen(); }}>
              Start course
            </button>
          </div>
        ) : (
          <>
            {course.nextUpSection && (
              <p className="course-card-next">
                Next · <span>{course.nextUpSection}</span>
              </p>
            )}
            <div className="course-card-progress">
              <div className="course-card-track"><div className="course-card-fill" style={{ width: `${pct}%` }} /></div>
              <span className="course-card-pct">{pct}%</span>
            </div>
          </>
        )}
      </footer>
    </article>
  );
}
